import { Injectable, signal, computed } from '@angular/core';
import { UserManager, User, WebStorageStateStore } from 'oidc-client-ts';
import { environment } from '#environments';

/**
 * Wraps oidc-client-ts UserManager for the Okta PKCE flow.
 * Each instance uses isolated sessionStorage namespaces so Okta state
 * never contaminates MSAL's storage (and vice versa).
 */
@Injectable({ providedIn: 'root' })
export class OktaAuthService {
    private readonly _userManager: UserManager;

    private _user = signal<User | null>(null);

    readonly isAuthenticated = computed(() => {
        const user = this._user();
        return user !== null && !user.expired;
    });

    readonly accessToken = computed(() => this._user()?.access_token ?? null);
    readonly email = computed(() => this._user()?.profile?.['email'] as string ?? '');
    readonly displayName = computed(() => this._user()?.profile?.name ?? '');
    /** Email claim used as the ARES userId for external users (preferred_username fallback, then sub). */
    readonly userId = computed(() =>
        (this._user()?.profile?.['email'] as string | undefined) ??
        (this._user()?.profile?.['preferred_username'] as string | undefined) ??
        this._user()?.profile?.sub ??
        ''
    );

    constructor() {
        this._userManager = new UserManager({
            authority: environment.okta.authority,
            client_id: environment.okta.clientId,
            redirect_uri: environment.okta.redirectUri,
            scope: environment.okta.scopes,
            response_type: 'code',
            // Isolated sessionStorage namespaces prevent cross-IdP state contamination.
            userStore: new WebStorageStateStore({ store: sessionStorage, prefix: 'oidc.okta:' }),
            stateStore: new WebStorageStateStore({ store: sessionStorage, prefix: 'oidc.okta.state:' }),
            automaticSilentRenew: true,
        });

        // Restore any existing session from sessionStorage on startup.
        this._userManager.getUser().then(user => {
            if (user && !user.expired) {
                this._user.set(user);
            }
        });

        this._userManager.events.addUserLoaded(user => this._user.set(user));
        this._userManager.events.addUserUnloaded(() => this._user.set(null));
        this._userManager.events.addAccessTokenExpired(() => this._user.set(null));
    }

    /** Initiates the Okta PKCE login redirect. */
    login(): Promise<void> {
        return this._userManager.signinRedirect();
    }

    /**
     * Processes the Okta authorization code redirect from the /auth callback URL.
     * Throws if there is no matching Okta state in sessionStorage (i.e. it was an Entra redirect).
     */
    handleCallback(): Promise<User> {
        return this._userManager.signinRedirectCallback().then(user => {
            this._user.set(user);
            return user;
        });
    }

    /** Clears the local Okta session without triggering an Okta end-session redirect. */
    logout(): Promise<void> {
        return this._userManager.removeUser().then(() => this._user.set(null));
    }
}
